// Inbox's own pages in Blueprint (pages/ux-full inbox/approval and inbox/notifications), on ui-web's
// ListPage pattern (BL-189), drawn with catalogued parts only (inbox-pages-model.ts builds their words):
// - To approve: one request on its own page (approvals.list), decided with approvals.decide exactly as
//   ApprovalsInbox decides it: Approve & run, "act as me for 12 hours" where it's offered, or Decline
//   with a note for the agent. The others still waiting are beside it, one tap each.
// - Notifications: the bell's list as a page (the same NotesState the bell reads); opening one marks it
//   read and goes where the bell's does; Mark read, Mark all read. What the agents did is beside it for
//   those who see the agents.
import { useEffect, useState } from 'react';
import { AgentActivity, AgentActivityProps, type AgentRequestProps } from '@flowology/ui-web';
import { Button, KeyValue, Label, Link, NoteList, Notice, RowList, TextField } from '@flowology/ui-web/fl';
import { ListPage, type ListStatus } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { officeHost } from '../blueprint/host.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { errorText, useQuery } from '../books/ui.tsx';
import { invoiceIdsIn } from '../books/model.ts';
import type { Approval } from '../books/types.ts';
import type { AgentsList } from '../agents/types.ts';
import { useOnline } from '../connection/connection.ts';
import { shownText } from '../shared/pages.ts';
import { useSaveKey } from '../shared/SaveButton.tsx';
import type { Note } from '../shared/notifications-model.ts';
import {
  decidedText,
  noteListProps,
  requestProps,
  shownRequest,
  waitingRows,
  type Decision,
} from './inbox-pages-model.ts';
import './inbox-pages.css';

/** Whoever holds agents.view sees the agents themselves, so their stopped state and what they did. */
const VIEWERS = ['agents.view'];

/** A region's state from what's read: an answer (or none yet) and the server's refusal. */
const stateOf = (ready: boolean, error: string | null): ListStatus =>
  ready ? 'ready' : error ? 'failed' : 'loading';

export function ApprovalScreen({
  nav,
  timeZone,
  onAllRequests,
}: {
  nav: NavProps;
  timeZone: string;
  /** Agents and approvals › Approvals: every request, decided ones too. */
  onAllRequests: () => void;
}) {
  const held = nav.permissions ?? [];
  const pending = useQuery<{ approvals: Approval[] }>('approvals.list', { status: 'pending' });
  const agents = useQuery<AgentsList>('agents.list', VIEWERS.some((p) => held.includes(p)) ? {} : null);
  const stopped = new Map(agents.data?.agents.map((a) => [a.key, a.stoppedReason]) ?? []);
  const [wanted, setWanted] = useState<string | null>(null);
  // a request just decided stays on the page, as approvals.decide answered it, until another is picked
  const [answered, setAnswered] = useState<Approval | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = pending.data?.approvals ?? [];
  const shown = answered && answered.id === wanted ? answered : shownRequest(list, wanted);
  const others = list.filter((a) => a.id !== shown?.id);
  // the invoices its input names, by number (ApprovalsInbox reads them the same way, QA-267)
  const [numbers, setNumbers] = useState<ReadonlyMap<string, string>>(new Map());
  const ids = shown ? invoiceIdsIn(shown.input).filter((id) => !numbers.has(id)) : [];
  const wantedIds = ids.join(',');
  useEffect(() => {
    if (!wantedIds) return;
    let live = true;
    void Promise.all(
      wantedIds.split(',').map((id) =>
        client
          .call<{ number: string }>('invoices.get', { id })
          .then((r) => [id, r.number] as const)
          .catch(() => null),
      ),
    ).then((got) => {
      if (live) setNumbers((m) => new Map([...m, ...got.filter((g): g is readonly [string, string] => !!g)]));
    });
    return () => {
      live = false;
    };
  }, [wantedIds]);
  // one key per attempt at the same decision, so pressing again after a lost answer decides it once (QA-058)
  const approveKey = useSaveKey(`approve:${shown?.id ?? ''}`);
  const trustKey = useSaveKey(`trust:${shown?.id ?? ''}`);
  const declineKey = useSaveKey(`decline:${shown?.id ?? ''}`);
  const decide = async (a: Approval, d: Decision) => {
    setBusy(true);
    setError(null);
    const key = d.decision === 'decline' ? declineKey : d.trust ? trustKey : approveKey;
    try {
      const out = await client.call<Approval>(
        'approvals.decide',
        {
          approvalId: a.id,
          decision: d.decision,
          ...(d.note ? { note: d.note } : {}),
          ...(d.trust ? { trustConnection: true } : {}),
        },
        { idempotencyKey: key.key },
      );
      key.renew();
      // the decision's answer names no records: the words its row had stay
      setAnswered({ ...out, labels: out.labels ?? a.labels ?? {} });
      setWanted(out.id);
      await pending.reload();
    } catch (e) {
      setError(errorText(e, 'That didn’t go through. Try again.'));
    } finally {
      setBusy(false);
    }
  };
  const pick = (id: string) => {
    setWanted(id);
    setAnswered(null);
    setError(null);
  };
  const props = shown
    ? requestProps(shown, {
        timeZone,
        names: numbers,
        stopped: stopped.get(shown.agent.replace(/^agent:/, '')) ?? null,
      })
    : null;
  const crumb = usePageCrumb(nav);
  const status = stateOf(!!pending.data, pending.error);
  const failed = pending.error ?? 'The requests didn’t load.';
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: props?.title ?? 'To approve',
            // the request says who asked and when, under its own title
            ...(props
              ? {}
              : {
                  lede: pending.data
                    ? 'Nothing waits for your OK.'
                    : 'What the agents and AI tools ask before they act.',
                }),
          }}
          list={{
            title: props || !pending.data ? 'The request' : 'Waiting for you',
            status: status === 'ready' && !props ? 'empty' : status,
            what: 'the requests',
            emptyText:
              'Nothing waits for your OK. Agents ask before anything that contacts a customer, books work or touches money, and their requests land here.',
            failedText: failed,
            onRetry: () => void pending.reload(),
            children: props && shown && (
              <>
                <OneRequest r={props} busy={busy} onDecide={(d) => void decide(shown, d)} />
                {error && (
                  <p className="ib-said-bad" role="alert">
                    {error}
                  </p>
                )}
              </>
            ),
          }}
          aside={{
            title: 'Also waiting',
            ...(pending.data ? { count: others.length } : {}),
            status,
            what: 'the requests',
            failedText: failed,
            onRetry: () => void pending.reload(),
            children: (
              <>
                <RowList
                  rows={waitingRows(others.slice(0, 5), { timeZone, onOpen: pick })}
                  label="Also waiting"
                />
                {!others.length && <p className="ib-note">Nothing else waits for you.</p>}
                <p className="ib-acts">
                  <Link onPress={onAllRequests}>
                    {others.length > 5
                      ? `All ${list.length} requests, and the decided ones`
                      : 'Every request, decided ones too'}
                  </Link>
                </p>
              </>
            ),
          }}
        />
      </main>
    </div>
  );
}

const DECIDED: Record<AgentRequestProps['state'], string> = {
  pending: 'Waiting for you',
  approved: 'Approved',
  declined: 'Declined',
  expired: 'Lapsed',
};

/** One request (approvals.list): who asked, why, exactly what will run, and the decision. */
export function OneRequest({
  r,
  busy,
  onDecide,
}: {
  r: AgentRequestProps;
  busy: boolean;
  onDecide: (d: Decision) => void;
}) {
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const waiting = r.state === 'pending';
  return (
    <article className="ib-req" aria-label={r.title}>
      <p className="ib-req__state">
        {r.money && <Label>{r.money}</Label>}
        {waiting ? (
          <Label kind="wait">Waiting for you</Label>
        ) : (
          <Label kind={r.state === 'approved' ? 'now' : 'muted'}>{DECIDED[r.state]}</Label>
        )}
      </p>
      <h3 className="ib-req__title" title={r.action}>
        {r.title}
      </h3>
      <p className="ib-req__sub">
        {r.asker.name}, asked {r.asked}
      </p>
      {r.flagged && (
        <p className="ib-req__line">
          <Label kind="bad">Instructions found in customer text</Label>
        </p>
      )}
      {r.explanation && (
        <p className="ib-req__line">
          <span className="ib-req__muted">Why: </span>
          {r.explanation}
        </p>
      )}
      {r.rows ? (
        <>
          <p className="ib-req__lead">Exactly what will run (it can’t be changed)</p>
          <KeyValue
            items={r.rows.map((x) => ({
              label: x.label,
              value: x.exact && x.exact !== x.text ? <span title={x.exact}>{x.text}</span> : x.text,
            }))}
          />
        </>
      ) : (
        <p className="ib-req__lead">The details show only to people who could do this themselves.</p>
      )}
      {r.money && waiting && (
        <p className="ib-req__money">
          Money: approving also needs the owner’s grant for {r.money.toLowerCase()}s.
        </p>
      )}
      {!waiting && (
        <div className="ib-req__said">
          <Notice tone="done" title={decidedText(r)}>
            {r.decided?.note ? `Note to the agent: “${r.decided.note}”` : null}
          </Notice>
        </div>
      )}
      {waiting &&
        (r.cannotDecide ? (
          <p className="ib-req__waits">{r.cannotDecide}</p>
        ) : declining ? (
          <div className="ib-req__decline">
            <TextField
              label="Note for the agent (it reads this next time)"
              value={note}
              maxLength={500}
              onChange={setNote}
            />
            <div className="ib-acts">
              <Button
                variant="primary"
                isBusy={busy}
                onPress={() =>
                  onDecide({ decision: 'decline', ...(note.trim() ? { note: note.trim() } : {}) })
                }
              >
                {busy ? 'Declining…' : 'Decline'}
              </Button>
              <Link isDisabled={busy} onPress={() => setDeclining(false)}>
                Back
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* the one primary: "act as me" where it's offered (the prototype's recommended way), else Approve */}
            <div className="ib-acts">
              {r.canTrust && (
                <Button
                  variant="primary"
                  isBusy={busy}
                  onPress={() => onDecide({ decision: 'approve', trust: true })}
                >
                  Approve, and let it act as me for {r.trustHours} hours
                </Button>
              )}
              {r.canTrust ? (
                <Link isDisabled={busy} onPress={() => onDecide({ decision: 'approve' })}>
                  {busy ? 'Approving…' : 'Approve & run'}
                </Link>
              ) : (
                <Button variant="primary" isBusy={busy} onPress={() => onDecide({ decision: 'approve' })}>
                  {busy ? 'Approving…' : 'Approve & run'}
                </Button>
              )}
              <Link isDisabled={busy} onPress={() => setDeclining(true)}>
                Decline…
              </Link>
            </div>
            {r.expires && <p className="ib-req__sub">It lapses {r.expires} if nobody decides.</p>}
          </>
        ))}
    </article>
  );
}

export function NotificationsScreen({
  nav,
  timeZone,
  onApprovals,
}: {
  nav: NavProps;
  timeZone: string;
  /** What the agents did links to what waits for a person: To approve. */
  onApprovals: () => void;
}) {
  const notes = nav.notes;
  const online = useOnline();
  const held = nav.permissions ?? [];
  const sees = VIEWERS.some((p) => held.includes(p));
  const activity = useQuery<unknown>('agents.activity', sees ? {} : null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // the last list shows at once; this brings it up to date, as the bell does on opening
  useEffect(() => {
    void notes?.reload();
  }, []);
  const list = notes?.notes ?? null;
  const unread = (list ?? []).filter((n) => !n.read).map((n) => n.id);
  const mark = async (key: string, ids: string[]) => {
    if (!notes || busy) return;
    setBusy(key);
    setError(null);
    try {
      await notes.markRead(ids);
    } catch (e) {
      setError(errorText(e, 'That didn’t go through. Try again.'));
    } finally {
      setBusy(null);
    }
  };
  const open = (n: Note) => {
    if (!n.read) void notes?.markRead([n.id]).catch(() => undefined);
    nav.onOpenNote?.(n);
  };
  const steps = activity.data ? AgentActivityProps.safeParse(activity.data) : null;
  const groups = list ? noteListProps(list, new Date(), timeZone).groups : [];
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Notifications',
            lede: list
              ? notes?.unread
                ? `${notes.unread} unread. Each opens where it’s dealt with.`
                : 'Nothing new. Each opens where it’s dealt with.'
              : 'What happened that you may want to know.',
            ...(unread.length > 0
              ? {
                  actions: (
                    <Button
                      isDisabled={!online || !!busy}
                      isBusy={busy === 'all'}
                      onPress={() => void mark('all', unread)}
                    >
                      {busy === 'all' ? 'Marking…' : 'Mark all read'}
                    </Button>
                  ),
                }
              : {}),
          }}
          {...(!online || error
            ? {
                notice: (
                  <>
                    {!online && (
                      <Notice title="No connection">New notifications arrive when it’s back.</Notice>
                    )}
                    {error && (
                      <p className="ib-said-bad" role="alert">
                        {error}
                      </p>
                    )}
                  </>
                ),
              }
            : {})}
          list={{
            title: 'Your notifications',
            ...(list ? { count: notes?.total ?? list.length } : {}),
            // NoteList says its own empty state, between dashed rules, as it always has
            status: list ? 'ready' : notes?.error ? 'failed' : 'loading',
            what: 'your notifications',
            failedText: notes?.error ?? 'Your notifications didn’t load.',
            onRetry: () => void notes?.reload(),
            children: list && (
              <>
                <NoteList
                  data={{ groups }}
                  busy={busy}
                  onOpen={(id) => {
                    const n = list.find((x) => x.id === id);
                    if (n) open(n);
                  }}
                  {...(online ? { onMarkRead: (id: string) => void mark(id, [id]) } : {})}
                />
                {(notes?.more || notes?.busy) && (
                  <p className="ib-acts">
                    <span className="ib-note">{shownText(list.length, notes.total)}</span>
                    <Link isDisabled={notes.busy} onPress={() => notes.more?.()}>
                      {notes.busy ? 'Loading…' : 'Load more'}
                    </Link>
                  </p>
                )}
              </>
            ),
          }}
          {...(steps?.success
            ? {
                aside: {
                  title: 'What the agents did today',
                  status: 'ready' as const,
                  what: 'what the agents did',
                  children: <AgentActivity data={steps.data} host={officeHost(() => onApprovals())} />,
                },
              }
            : {})}
        />
      </main>
    </div>
  );
}
